import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../api/endpoints';
import { useAuthStore } from '../stores/auth.store';
import type { Inventory } from '@motra/shared';

export function useInventory(shopId?: string, category?: string) {
  const shop = useAuthStore((s) => s.shop);
  const activeShopId = shopId ?? shop?.id;

  return useQuery({
    queryKey: ['inventory', activeShopId, category],
    queryFn: () => inventoryApi.list(activeShopId!, { category }),
    enabled: !!activeShopId,
    select: (res) => res.data ?? [],
  });
}

export function useUpsertInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Inventory> & { shopid?: string }) =>
      inventoryApi.upsert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useDeleteInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inventoryId: string) => inventoryApi.delete(inventoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useBatchUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shopId, items }: { shopId: string; items: Partial<Inventory>[] }) =>
      inventoryApi.batch(shopId, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      inventory_id: string;
      source_shop_id: string;
      dest_shop_id: string;
      units: number;
    }) => inventoryApi.transfer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['transit'] });
    },
  });
}

export function useDepletedInventory(shopId?: string) {
  const shop = useAuthStore((s) => s.shop);
  const activeShopId = shopId ?? shop?.id;

  return useQuery({
    queryKey: ['inventory-depleted', activeShopId],
    queryFn: () => inventoryApi.depleted(activeShopId!),
    enabled: !!activeShopId,
    select: (res) => res.data ?? [],
  });
}

export function useInventoryImages(inventoryId?: string) {
  return useQuery({
    queryKey: ['inventory-images', inventoryId],
    queryFn: () => inventoryApi.getImages(inventoryId!),
    enabled: !!inventoryId,
    select: (res) => res.data ?? [],
  });
}
